import React, { useState, useCallback, useEffect, useContext } from "react";
import { gql, useQuery } from "urql";
import { useParams, useLocation } from "wouter";
import { useQueryState } from "../../../../components/src/use-query-state";
import * as Tabs from "@radix-ui/react-tabs";
import { TransactionsPage } from "../TransactionsPage";
import { ReconciliationPage } from "../ReconciliationPage";
import { DealBlotterGrid } from "../DealBlotterGrid";

import { DateForm } from "../../../../components/src/dateForm";
import { MiniTransactionForm, miniTransactionTypes, NewMiniTransactionButton } from "../MiniTransactionForm";
import { serializeSwedenDate } from "../../../../components/src/dateFormater";
import { PartyType } from "../../types.generated";
import { ClientContextSelector } from "../../contexts/ClientContextSelector";
import { ClientContext } from "../../../../components/src/contexts/ClientContext";
import { Panel } from "../../../../components/src/common/Panel";
import { ResponsiveTabsList } from "../../../../components/src/ResponsiveTabsList";

const getClient = gql`
    query {
        clients: parties(filter: { typeIn: [Client] }) {
            _id
            name
            accountingCurrency
            fundInfo {
                classes {
                    startDate
                }
            }
        }
    }
`;

export interface PartiesParamTypes {
    clientDashName: string;
    tabId?: string;
    transId?: string;
}

const tabs = {
    dealblotter: "dealblotter",
    transactions: "transactions",
    reconciliation: "reconciliation"
};

export function OperationsTabPage(): React.ReactElement {
    const [startDate] = useQueryState("startDate", serializeSwedenDate(new Date(new Date().getFullYear() - 1, 0, 1)));
    const [id] = useQueryState("id", "");

    const [location, navigate] = useLocation();
    const { tabId, transId } = useParams<{ tabId: string; transId: string }>();
    const [activeTabId, setActiveTabId] = useState(tabId);
    const { client: clientContext } = useContext(ClientContext);

    const [{ fetching, error, data }] = useQuery({ query: getClient });

    useEffect(() => {
        if (clientContext && clientContext.dashName) {
            if (!(tabId in tabs)) {
                navigate("/operations/" + clientContext.dashName + "/dealblotter");
                setActiveTabId("dealblotter");
            } else {
                if (tabId !== activeTabId) {
                    setActiveTabId(tabId);
                }
            }
        } /*else if (clientContext._id === emptyObjectId && (tabId || startDate || id)) {
            navigate("/operations/" + clientContext.dashName, { replace: true });
        }*/
    }, [tabId, data, setActiveTabId, clientContext, navigate, startDate, id, activeTabId]);

    const onTabChange = useCallback(
        (tabId) => {
            //const types = data && data.party ? cloneDeep(data.party.types) : null;
            //const tabs = availableTabs(types);
            if (clientContext && clientContext.dashName) {
                if (!(tabId in tabs)) {
                    navigate("/operations/" + clientContext.dashName + "/dealblotter", { replace: true });
                    setActiveTabId("dealblotter");
                } else {
                    if (tabId !== activeTabId) {
                        navigate("/operations/" + clientContext.dashName + "/" + tabId, { replace: true });
                        setActiveTabId(tabId);
                    }
                }
            } else {
                if (!(tabId in tabs)) {
                    navigate("/operations/" + "/dealblotter", { replace: true });
                    setActiveTabId("dealblotter");
                } else {
                    if (tabId !== activeTabId) {
                        navigate("/operations/" + tabId, { replace: true });
                        setActiveTabId(tabId);
                    }
                }
            }
        },
        [setActiveTabId, activeTabId, clientContext, navigate]
    );

    if (fetching)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    // client
    const clientName = clientContext && clientContext.dashName ? clientContext.dashName.replaceAll("-", " ") : "-";
    const client = data.clients.find((c) => c.name == clientName);
    const miniTransactionType =
        id && id.startsWith("new")
            ? miniTransactionTypes.find((d) => d.toLowerCase() === id.substring(3, id.length))
            : miniTransactionTypes[0];

    const [pathname] = location.split("?");

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full sm:w-1/3 px-2 mb-4">
                    <ClientContextSelector typeIn={[PartyType.Client]} />
                </div>
            </div>
            {client ? (
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full px-2 bg-slate-100 rounded-lg p-4 shadow-sm">
                        <Tabs.Root value={activeTabId || "dealblotter"} onValueChange={onTabChange} className="w-full">
                            <ResponsiveTabsList value={activeTabId || "dealblotter"} onValueChange={onTabChange}>
                                <Tabs.Trigger
                                    value="dealblotter"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    dealblotter
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="transactions"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    transactions
                                </Tabs.Trigger>
                                <Tabs.Trigger
                                    value="reconciliation"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    reconciliation
                                </Tabs.Trigger>
                            </ResponsiveTabsList>

                            <Tabs.Content value="dealblotter" className="mt-4">
                                <div className="flex items-start gap-3">
                                    <DateForm defaultDateString={startDate} dateName={"startDate"}></DateForm>
                                    <div className="ml-auto pt-2">
                                        <NewMiniTransactionButton page={location} queryArgMode={true} />
                                    </div>
                                </div>
                                <div className="flex flex-wrap -mx-2">
                                    <div className="w-full px-2">
                                        <DealBlotterGrid startDate={startDate} clientId={client._id} />
                                    </div>
                                </div>
                                <Panel
                                    title={miniTransactionType}
                                    isOpen={!!(transId || id)}
                                    onDismiss={() => navigate(pathname.replaceAll(transId, ""))}
                                >
                                    {transId || id ? <MiniTransactionForm id={transId} type={miniTransactionType} /> : null}
                                </Panel>
                            </Tabs.Content>

                            <Tabs.Content value="transactions" className="mt-4">
                                <TransactionsPage />
                            </Tabs.Content>

                            <Tabs.Content value="reconciliation" className="mt-4">
                                <ReconciliationPage />
                            </Tabs.Content>
                        </Tabs.Root>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
